import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowRight, Trophy, Users, Ticket, Spade, BarChart3, Newspaper, MessageCircle, Mail, Loader2, Lock, CalendarDays, Flame } from "lucide-react";
import { API, ASSETS, DISCORD_URL } from "@/lib/gp";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { usePageMeta } from "@/hooks/usePageMeta";
import { PLATFORM_META } from "@/seo/launchPages";

// Planned game hubs. None of these has a published hub page yet, so every card
// states the exact missing piece instead of pretending the hub is live.
const PLANNED_HUBS = [
  { id: "slay-the-spire", name: "Slay the Spire", missing: "the Slay the Spire hub route is not published" },
  { id: "hades", name: "Hades", missing: "the Hades hub route is not published" },
  { id: "marvel-snap", name: "Marvel Snap", missing: "the Marvel Snap hub route is not published" },
  { id: "vampire-survivors", name: "Vampire Survivors", missing: "the Vampire Survivors hub route is not published" },
  { id: "battlefield-6", name: "Battlefield 6", missing: "the Battlefield 6 hub route is not published" },
];

export default function Home() {
  usePageMeta(PLATFORM_META["/"]);
  const [event, setEvent] = useState(null);
  const [leaders, setLeaders] = useState([]);
  const [news, setNews] = useState([]);
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/next-event`).then((r) => r.json()).then(setEvent).catch(() => {});
    fetch(`${API}/leaderboard`).then((r) => r.json()).then((d) => setLeaders((d.leaderboard || []).slice(0, 5))).catch(() => {});
    fetch(`${API}/news`).then((r) => r.json()).then((d) => setNews((d.news || []).slice(0, 3))).catch(() => {});
  }, []);

  const joinList = async (e) => {
    e.preventDefault();
    if (!email) return;
    setBusy(true);
    try {
      const res = await fetch(`${API}/newsletter`, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ email }) });
      if (!res.ok) throw new Error();
      toast.success("You're on the list!"); setEmail("");
    } catch { toast.error("Try again with a valid email."); } finally { setBusy(false); }
  };

  return (
    <div className="relative">
      <div className="md:hidden fixed inset-0 z-0 bg-cover bg-[position:62%_25%]" style={{ backgroundImage: `url(${ASSETS.bg})`, filter: "brightness(1.16) saturate(1.18)" }} data-testid="yorick-bg-mobile" />
      <div className="hidden md:block fixed top-[116px] bottom-0 left-[24%] right-0 z-0 bg-contain bg-top bg-no-repeat" style={{ backgroundImage: `url(${ASSETS.bg})`, filter: "brightness(1.28) saturate(1.24) contrast(1.04)" }} data-testid="yorick-bg" />
      <div className="md:hidden fixed inset-0 z-0" style={{ background: "linear-gradient(90deg, rgba(7,11,18,0.90) 0%, rgba(7,11,18,0.62) 58%, rgba(7,11,18,0.38) 100%)" }} />
      <div className="hidden md:block fixed inset-0 z-0" style={{ background: "linear-gradient(90deg, rgba(7,11,18,0.97) 0%, rgba(7,11,18,0.72) 38%, rgba(7,11,18,0.12) 57%, rgba(7,11,18,0.02) 100%)" }} />
      <div className="fixed inset-0 z-0" style={{ background: "linear-gradient(0deg, rgba(7,11,18,0.72) 0%, transparent 30%)" }} />

      <div className="relative z-10">
        <section className="mx-auto max-w-[1440px] px-5 md:px-8 pt-16 md:pt-20 pb-8 min-h-[64vh] flex flex-col justify-center">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-2xl">
            <span className="inline-flex items-center gap-2 rounded-full border border-gp-blue/30 bg-gp-blue/10 px-4 py-1.5 text-xs font-semibold text-gp-blue">
              <span className="h-1.5 w-1.5 rounded-full bg-gp-blue animate-pulse-glow" /> Compete · Discover · Conquer
            </span>
            <h1 className="mt-6 font-unbounded font-black tracking-tight text-4xl sm:text-5xl lg:text-6xl leading-[1.02] uppercase">
              Your Balatro coach<br /><span className="text-gp-blue text-glow-blue">and tournament home.</span>
            </h1>
            <p className="mt-5 text-white/75 text-lg max-w-xl leading-relaxed">
              Yorick is the AI coach and tournament home for Balatro players. Ask about your run, share your best builds, and compete in GamePlat community challenges.
            </p>
            <div className="mt-8 flex flex-wrap gap-4">
              <Button onClick={() => navigate("/balatro")} className="rounded-full h-13 px-7 py-6 text-base bg-gp-blue hover:bg-gp-blue/90 text-white font-bold shadow-[0_0_28px_rgba(22,119,255,0.45)] transition-transform hover:-translate-y-0.5">
                Explore the Balatro hub <Spade size={18} className="ml-2" />
              </Button>
              <Button data-testid="hero-register" onClick={() => navigate("/tournaments")} variant="outline" className="rounded-full h-13 px-7 py-6 text-base border-white/20 bg-white/5 hover:bg-white/10 text-white font-semibold">
                Join tournament waitlist <Trophy size={18} className="ml-2" />
              </Button>
              <a data-testid="hero-postabunch" href="https://post.gameplatplay.net/?open=app&utm_source=gameplatplay&utm_medium=home_hero"
                className="inline-flex items-center rounded-full h-13 px-7 py-6 text-base bg-gp-blue hover:bg-gp-blue/90 text-white font-bold shadow-[0_0_28px_rgba(22,119,255,0.45)] transition-transform hover:-translate-y-0.5">
                POSTABUNCH SIGN IN <ArrowRight size={18} className="ml-2" />
              </a>
              <Button onClick={() => navigate("/chat")} variant="outline" className="rounded-full h-13 px-7 py-6 text-base border-gp-gold/35 bg-gp-gold/10 hover:bg-gp-gold/20 text-gp-gold font-semibold">Open Yorick chat</Button>
            </div>
          </motion.div>
        </section>

        <section className="mx-auto max-w-[1440px] px-5 md:px-8 py-4">
          <div className="glass rounded-3xl p-8 md:p-10 relative overflow-hidden ring-1 ring-gp-gold/30" data-testid="gta6-home-feature">
            <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(700px 260px at 90% 0%, rgba(255,180,0,0.24), transparent)" }} />
            <div className="relative grid lg:grid-cols-[1.25fr_.75fr] gap-8 items-center">
              <div>
                <span className="font-vt323 uppercase tracking-widest text-gp-gold text-sm">GTA VI Launch Watch</span>
                <h2 className="font-unbounded font-black tracking-tighter text-3xl md:text-5xl uppercase mt-1">GTA 6 Command Center</h2>
                <p className="mt-3 text-white/65 max-w-2xl">Release date, pre-orders, editions, Jason and Lucia, Vice City and Leonida, PC status, performance questions and every major confirmed update as November 19 approaches.</p>
                <div className="mt-5 flex flex-wrap gap-3 text-sm">
                  <Link to="/gta-6/release-date" className="rounded-full bg-white/5 border border-white/10 px-4 py-2 hover:border-gp-blue/40">Release date</Link>
                  <Link to="/gta-6/pre-order" className="rounded-full bg-white/5 border border-white/10 px-4 py-2 hover:border-gp-blue/40">Pre-order</Link>
                  <Link to="/gta-6/map-and-locations" className="rounded-full bg-white/5 border border-white/10 px-4 py-2 hover:border-gp-blue/40">Map & locations</Link>
                  <Link to="/gta-6/pc" className="rounded-full bg-white/5 border border-white/10 px-4 py-2 hover:border-gp-blue/40">PC status</Link>
                </div>
              </div>
              <div className="lg:text-right">
                <p className="text-white/45 text-sm uppercase tracking-widest">Current launch date</p>
                <p className="font-unbounded font-black text-2xl mt-1">November 19, 2026</p>
                <Button onClick={() => navigate("/gta-6")} className="mt-5 rounded-full bg-gp-blue font-bold px-7">View all GTA 6 coverage <ArrowRight size={16} className="ml-2" /></Button>
              </div>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1440px] px-5 md:px-8 py-8">
          <div className="glass rounded-3xl p-8 md:p-10 relative overflow-hidden ring-1 ring-gp-blue/25" data-testid="next-event-banner">
            <div className="absolute inset-0 opacity-30" style={{ background: "radial-gradient(600px 240px at 90% 0%, rgba(22,119,255,0.28), transparent)" }} />
            <div className="relative flex flex-col md:flex-row md:items-center gap-6 justify-between">
              <div>
                <span className="font-vt323 uppercase tracking-widest text-gp-gold text-sm">Upcoming Tournament</span>
                <h2 className="font-unbounded font-black tracking-tighter text-2xl md:text-4xl uppercase mt-1">{event?.event?.name || "GamePlat Balatro Community Challenge"}</h2>
                <p className="mt-2 text-white/60 text-sm max-w-xl">{event?.event?.date_note || "December 12, 2026. Join the waitlist to hold a seat."}</p>
                <div className="mt-3 flex flex-wrap gap-4 text-white/75 text-sm">
                  <span className="flex items-center gap-1.5"><CalendarDays size={15} className="text-gp-gold" /> {event?.event?.date_label || "December 12, 2026"}</span>
                  <span className="flex items-center gap-1.5"><Users size={15} className="text-gp-blue" /> Up to {event?.event?.players || 64} seats</span>
                  <span className="flex items-center gap-1.5"><Ticket size={15} className="text-gp-green" /> {event?.event?.entry || "Free Entry"}</span>
                  <span className="flex items-center gap-1.5"><Trophy size={15} className="text-gp-gold" /> {event?.event?.type || "Community Competition"}</span>
                </div>
              </div>
              <Button data-testid="banner-register" onClick={() => navigate("/tournaments")} className="rounded-full h-13 px-8 py-6 text-base bg-gp-blue hover:bg-gp-blue/90 font-bold shrink-0 shadow-[0_0_22px_rgba(22,119,255,0.45)]">Join Waitlist</Button>
            </div>
          </div>
        </section>

        <section className="mx-auto max-w-[1440px] px-5 md:px-8 py-8">
          <h2 className="font-unbounded font-bold text-2xl md:text-3xl tracking-tighter uppercase mb-2">Game Hubs</h2>
          <p className="text-white/50 text-sm mb-6 max-w-3xl">
            Balatro and GTA 6 are live. The other hubs are planned — they say what is missing until the page ships, and Yorick already covers each game.
          </p>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <button onClick={() => navigate("/gta-6")} data-testid="supported-gta6" className="group relative text-left rounded-2xl p-6 h-40 overflow-hidden ring-1 ring-gp-blue/50 hover:-translate-y-1 transition-all" style={{ background: "linear-gradient(155deg, rgba(255,180,0,0.18), rgba(22,119,255,0.14))" }}>
              <Flame size={90} className="absolute -right-3 -top-2 opacity-20 text-gp-gold group-hover:opacity-40 transition-opacity" />
              <span className="text-[11px] uppercase tracking-widest text-gp-gold">Hot now</span>
              <h3 className="font-unbounded font-bold text-xl mt-2">GTA VI</h3>
              <p className="text-sm text-white/60 mt-1">27 live guides →</p>
            </button>
            <button onClick={() => navigate("/balatro")} data-testid="supported-balatro" className="group relative text-left rounded-2xl p-6 h-40 overflow-hidden ring-1 ring-gp-gold/40 hover:-translate-y-1 transition-all" style={{ background: "linear-gradient(155deg, rgba(22,119,255,0.20), rgba(14,165,233,0.12))" }}>
              <Spade size={90} className="absolute -right-3 -top-2 opacity-20 text-gp-gold group-hover:opacity-40 transition-opacity" />
              <span className="text-[11px] uppercase tracking-widest text-gp-gold">Featured</span>
              <h3 className="font-unbounded font-bold text-xl mt-2">Balatro</h3>
              <p className="text-sm text-white/60 mt-1">Guides & competition →</p>
            </button>
            {PLANNED_HUBS.map((g) => (
              <div
                key={g.id}
                data-testid={`planned-hub-${g.id}`}
                className="rounded-2xl p-6 h-40 ring-1 ring-white/10 bg-white/[0.03] flex flex-col justify-between"
              >
                <div className="flex items-start justify-between gap-3">
                  <Lock size={18} className="text-white/30 shrink-0" />
                  <span className="shrink-0 rounded-full border border-amber-300/35 bg-amber-300/10 px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest text-amber-300">
                    Not working yet
                  </span>
                </div>
                <div>
                  <h3 className="font-unbounded font-bold text-lg text-white/80">{g.name}</h3>
                  <p className="text-xs text-white/45 mt-1 leading-snug">Coming soon — {g.missing}.</p>
                  <Link to={`/game/${g.id}`} className="inline-flex items-center gap-1 mt-2 text-xs font-semibold text-gp-blue hover:underline">
                    Ask Yorick about {g.name} <ArrowRight size={12} />
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mx-auto max-w-[1440px] px-5 md:px-8 py-8 grid lg:grid-cols-2 gap-6">
          <div className="glass rounded-3xl p-7">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-unbounded font-bold text-xl flex items-center gap-2"><BarChart3 size={20} className="text-gp-blue" /> Leaderboard</h2>
              <Link to="/leaderboard" className="text-sm text-white/60 hover:text-white flex items-center gap-1">Full board <ArrowRight size={14} /></Link>
            </div>
            {leaders.length === 0 ? (
              <p className="text-white/45 text-sm py-6">The board fills after verified competition results. Join the waitlist and play to rank.</p>
            ) : (
              <div className="divide-y divide-white/10">
                {leaders.map((p, i) => (
                  <div key={p.user_id} className="flex items-center gap-3 py-2.5">
                    <span className="w-6 text-center font-unbounded font-black text-gp-gold">{i + 1}</span>
                    <span className="flex-1 font-semibold truncate">{p.name || "Player"}</span>
                    <span className="font-bold text-gp-blue">{p.points} pts</span>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="glass rounded-3xl p-7">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-unbounded font-bold text-xl flex items-center gap-2"><Newspaper size={20} className="text-gp-blue" /> Latest Content</h2>
              <Link to="/news" className="text-sm text-white/60 hover:text-white flex items-center gap-1">All news <ArrowRight size={14} /></Link>
            </div>
            <Link to="/gta-6" className="block rounded-xl border border-gp-gold/20 bg-gp-gold/5 px-4 py-3 mb-3 hover:border-gp-gold/40 transition-colors">
              <p className="text-xs uppercase tracking-widest text-gp-gold">GTA VI Launch Watch</p>
              <p className="font-semibold mt-1">27 live GTA 6 guides are now in the GamePlatPlay command center →</p>
            </Link>
            {news.length === 0 ? (
              <p className="text-white/45 text-sm py-4">Fresh gaming posts will show here. For now, explore the GTA 6 command center or Balatro guides.</p>
            ) : (
              <div className="space-y-3">
                {news.map((n) => (
                  <Link key={n.id} to={n.path || "/news"} className="block rounded-xl bg-black/25 px-4 py-3 hover:bg-black/40 transition-colors">
                    <p className="font-semibold text-sm">{n.title}</p>
                    <p className="text-xs text-white/55 mt-1 line-clamp-2">{n.body}</p>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </section>

        <section className="mx-auto max-w-[1440px] px-5 md:px-8 py-8 pb-16 grid md:grid-cols-2 gap-6">
          <div className="glass rounded-3xl p-8 flex flex-col">
            <MessageCircle className="text-[#5865F2]" size={26} />
            <h3 className="font-unbounded font-bold text-2xl mt-4">Join the Community</h3>
            <p className="text-white/60 mt-2 flex-1">{DISCORD_URL ? "Find teammates, get updates, and talk strategy with players on our Discord." : "Discord is not connected yet. Use the waitlist and newsletter until then."}</p>
            {DISCORD_URL ? (
              <a href={DISCORD_URL} target="_blank" rel="noreferrer" data-testid="home-discord" className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-[#5865F2] hover:bg-[#4752c4] px-6 py-3 font-bold transition-colors">
                <MessageCircle size={18} /> Connect Discord
              </a>
            ) : (
              <span data-testid="home-discord" className="mt-5 inline-flex w-fit items-center gap-2 rounded-full bg-white/10 px-6 py-3 font-bold text-white/55">
                <MessageCircle size={18} /> Discord coming later
              </span>
            )}
          </div>
          <div className="glass rounded-3xl p-8">
            <Mail className="text-gp-blue" size={26} />
            <h3 className="font-unbounded font-bold text-2xl mt-4">Get gaming alerts</h3>
            <p className="text-white/60 mt-2">GTA 6 updates, new guides, tournaments and Yorick drops — straight to your inbox.</p>
            <form onSubmit={joinList} className="mt-5 flex gap-3" data-testid="newsletter-form">
              <Input data-testid="newsletter-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@email.com" required className="bg-black/30 border-white/10 h-12 rounded-full px-5" />
              <Button type="submit" data-testid="newsletter-submit" disabled={busy} className="rounded-full h-12 px-6 bg-gp-blue hover:bg-gp-blue/90 font-bold">{busy ? <Loader2 className="animate-spin" size={18} /> : "Join"}</Button>
            </form>
            <p className="mt-3 text-xs leading-relaxed text-white/40">
              By joining, you agree to receive GamePlat updates. You can opt out anytime through <Link to="/support" className="text-gp-blue hover:underline">Support</Link>. See our <Link to="/privacy" className="text-gp-blue hover:underline">Privacy Policy</Link>.
            </p>
          </div>
        </section>
      </div>
    </div>
  );
}
